import type {
  OpenMainContentTabInput,
  Tab,
} from '../../store/layoutStoreTypes';

export const RECENT_ITEMS_LIMIT = 5;

export type RecentItemTabType =
  | 'finding'
  | 'signal'
  | 'executions'
  | 'agent-execution'
  | 'endpoint'
  | 'webpage'
  | 'surface-asset'
  | 'ai-component';

export type RecentItemTab = Extract<
  OpenMainContentTabInput,
  { type: RecentItemTabType }
>;

export type RecentItem = {
  key: string;
  tab: RecentItemTab;
  visitedAt: number;
};

const toRecentItemTab = (tab: Tab): RecentItemTab | null => {
  switch (tab.type) {
    case 'finding':
      return {
        id: tab.id,
        title: tab.title,
        type: tab.type,
        data: { findingId: tab.data.findingId, mode: 'view' },
      };
    case 'signal':
      return { id: tab.id, title: tab.title, type: tab.type, data: tab.data };
    case 'executions':
      return { id: tab.id, title: tab.title, type: tab.type, data: tab.data };
    case 'agent-execution':
      return { id: tab.id, title: tab.title, type: tab.type, data: tab.data };
    case 'endpoint':
      return { id: tab.id, title: tab.title, type: tab.type, data: tab.data };
    case 'webpage':
      return { id: tab.id, title: tab.title, type: tab.type, data: tab.data };
    case 'surface-asset':
      return { id: tab.id, title: tab.title, type: tab.type, data: tab.data };
    case 'ai-component':
      return { id: tab.id, title: tab.title, type: tab.type, data: tab.data };
    default:
      return null;
  }
};

const getRecentItemKey = (tab: RecentItemTab): string => {
  switch (tab.type) {
    case 'finding':
      return `finding:${tab.data.findingId}`;
    case 'signal':
      return `signal:${tab.data.signalId}`;
    case 'executions':
      return `executions:${tab.data.threadId}`;
    case 'agent-execution':
      return `agent-execution:${tab.data.executionId}`;
    case 'endpoint':
      return `endpoint:${tab.data.endpointId}`;
    case 'webpage':
      return `webpage:${tab.data.id}`;
    case 'surface-asset':
      return `surface-asset:${tab.data.surfaceAssetKind}:${tab.data.entityId}`;
    case 'ai-component':
      return `ai-component:${tab.data.componentId}`;
  }
};

export const toRecentItem = ({
  tab,
  visitedAt,
}: {
  tab: Tab;
  visitedAt: number;
}): RecentItem | null => {
  const recentTab = toRecentItemTab(tab);

  return recentTab
    ? { key: getRecentItemKey(recentTab), tab: recentTab, visitedAt }
    : null;
};

export const pushRecentItem = ({
  items,
  item,
}: {
  items: readonly RecentItem[];
  item: RecentItem;
}): RecentItem[] =>
  [item, ...items.filter((existing) => existing.key !== item.key)].slice(
    0,
    RECENT_ITEMS_LIMIT,
  );
